/// <amd-module name="Repos/PipelineStatus/Components/PipelineStatusPanel" />
import * as React from "react";
import { GitStatus } from "Repos/Common/Contracts/Git";
import * as Resources from "Repos/PipelineStatus/Resources";
import {
    getGitStatusStateProps,
    getReleaseGitStatusesSortedByRank,
    getReleaseSummaryUrl,
    processGitStatuses
} from "Repos/PipelineStatus/Util/GitStatusState";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListItemDetails, ListItem, ListSelection, ScrollableList } from "VSSUI/List";
import { Panel } from "VSSUI/Panel";
import { Status, StatusSize } from "VSSUI/Status";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface IPipelineStatusPanelProps {
    statuses?: GitStatus[];
    onDismiss: () => void;
}

interface IStatusItem {
    definitionName?: string;
    statuses: GitStatus[];
}

export class PipelineStatusPanel extends VssComponent<IPipelineStatusPanelProps> {
    private selection = new ListSelection(true);
    private listItems: IStatusItem[] = [];

    constructor(props: IPipelineStatusPanelProps, context: IVssComponentContext) {
        super(props, context);

        // Process the statuses.  Put all non release statuses first.  Then group
        // the release statuses by definition name and then add one row per definition.
        if (props.statuses) {
            const releaseStatuses: { [key: string]: GitStatus[] } = {};
            const nonReleaseStatuses: GitStatus[] = [];
            processGitStatuses(props.statuses, releaseStatuses, nonReleaseStatuses, this.context.pageContext);
            this.listItems.push(
                ...nonReleaseStatuses.map(val => {
                    return {
                        statuses: [val]
                    };
                })
            );

            this.listItems.push(
                ...Object.keys(releaseStatuses).map(definition => {
                    return {
                        statuses: releaseStatuses[definition],
                        definitionName: definition
                    };
                })
            );
        }
    }

    public render() {
        return (
            <Panel onDismiss={this._onDismiss} titleProps={{ text: Resources.Pipelines }}>
                <ScrollableList
                    itemProvider={new ArrayItemProvider(this.listItems)}
                    outerClassName="custom-scrollbar scroll-auto-hide"
                    renderRow={this.renderRow}
                    selection={this.selection}
                    width="100%"
                />
            </Panel>
        );
    }

    private renderRow = (index: number, item: IStatusItem, details: IListItemDetails<IStatusItem>, key?: string): JSX.Element => {
        const pipelineNameStartsWithProject = item.statuses[0].description.startsWith("[");
        return (
            <ListItem key={key || "list-item" + index} index={index} details={details}>
                <div className="repos-pipeline-status-item flex-row flex-grow rhythm-horizontal-8 padding-8 scroll-hidden">
                    {item.definitionName ? (
                        <ReleaseStatusRow pageContext={this.context.pageContext} statuses={item.statuses} defintionName={item.definitionName} />
                    ) : (
                        <StatusRow status={item.statuses[0]} showCrossProjectWarning={pipelineNameStartsWithProject} />
                    )}
                </div>
            </ListItem>
        );
    };

    private _onDismiss = () => {
        this.props.onDismiss();
    };
}

const StatusRow = (props: { status: GitStatus; showCrossProjectWarning: boolean }) => {
    const gitStatusStateProps = getGitStatusStateProps(props.status);
    var projectName: string = "";
    if (props.showCrossProjectWarning) {
        const endOfBrackets = props.status.description.indexOf("]");
        projectName = props.status.description.slice(1, endOfBrackets);
    }

    return (
        <>
            <Icon className="repos-pipeline-status-icon" iconName={"Build"} size={IconSize.medium} />
            <div className="flex-column flex-grow h-scroll-hidden">
                <Link className="flex" href={props.status.targetUrl} title={props.status.description}>
                    <span className="text-ellipsis">{props.status.description}</span>
                    <Status {...gitStatusStateProps.statusProps} size={StatusSize.m} className="flex-self-center margin-horizontal-8" />
                </Link>
                <span className="text-ellipsis secondary-text">{format("{0}/{1}", props.status.context.genre, props.status.context.name)}</span>
            </div>
            {props.showCrossProjectWarning && 
                <Icon 
                    ariaLabel={format(Resources.PipelinesCrossProjectWarning, projectName)}
                    className="repos-pipeline-project-warning flex-end"
                    iconName={"Info"}
                    size={IconSize.medium}
                    tabIndex={0}
                    tooltipProps={{addAriaDescribedBy: true, text: format(Resources.PipelinesCrossProjectWarning, projectName)}}/>
            }
        </>        
    );
};

const ReleaseStatusRow = (props: { pageContext: IVssPageContext; statuses: GitStatus[]; defintionName: string }) => {
    const sortedStatuses = getReleaseGitStatusesSortedByRank(props.statuses);
    return (
        <>
            <Icon className="repos-pipeline-status-icon" iconName={"Deploy"} size={IconSize.medium} />
            <div className="flex-column h-scroll-hidden">
                <Link className="flex" href={getReleaseSummaryUrl(props.pageContext, sortedStatuses)} aria-label={sortedStatuses[0].description}>
                    <span className="text-ellipsis">{props.defintionName}</span>
                </Link>
                <span className="text-ellipsis secondary-text">{format("{0}/{1}", props.statuses[0].context.genre, props.defintionName)}</span>
                <div className="repos-pipeline-status-container flex flex-wrap rhythm-vertical-8">
                    {sortedStatuses.map((status, index) => (
                        <div key={"status-" + index + status.context.name}>
                            <Link aria-label={status.description} href={status.targetUrl} tooltipProps={{ text: status.description }}>
                                <Status {...getGitStatusStateProps(status).statusProps} size={StatusSize.m} className="margin-right-16" />
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};
